/**
 * CardFeePayment — the embedded Stripe Payment Element for S7 (B5).
 *
 * MODEL: authorise, don't capture. The intent is created server-side with
 * capture_method='manual'; confirming here places a HOLD. Money only moves when
 * the bank approves (capture) or is released when it rejects (cancel).
 *
 * The amount is display-only here: it arrives from the server, which resolved it
 * from programme config. The component has no way to influence it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { loadStripe, type Stripe as StripeJs } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { createCardPayment, type CardPaymentIntent } from "@/services/payments";
import { PrimaryButton, StickyBar } from "@/components/consumer/chrome";
import { recordSignal } from "@/lib/funnel-signals";

export type PaymentPhase = "loading" | "ready" | "confirming" | "authorised" | "error";

interface Props {
  programId: string;
  attemptId: string;
  /** Bank-handoff figure. Passed for metadata only; never added to the charge. */
  intendedLoadAmountCents?: number | null;
  /** Cards on this order. The server resolves the AMOUNT from it; we send no amount. */
  quantity?: number | null;
  /** Called with the PaymentIntent id once funds are held (requires_capture). */
  onAuthorised: (paymentIntentId: string) => void | Promise<void>;
  /** Bubble up so S7's sticky CTA can reflect payment state. */
  onPhaseChange?: (phase: PaymentPhase) => void;
  submitting?: boolean;
  buttonLabel: string;
  confirmingLabel: string;
  authorisedLabel: string;
  timeoutMessage: string;
  /** COUNSEL-OWNED (`s7.security.notice`). Rendered directly under the card
      form — the moment the customer is entering a card is when this matters.
      Stripe's own mandate text is off (`terms: { card: "never", … }`), so this
      is the ONLY statement about card handling on the screen. */
  securityNotice: string;
}

export const PAYMENT_CONFIRM_TIMEOUT_MS = 45_000;

class PaymentConfirmationTimeout extends Error {
  constructor(readonly stage: "elements_submit" | "confirm_payment") {
    super(`payment_confirmation_timeout:${stage}`);
    this.name = "PaymentConfirmationTimeout";
  }
}

export async function withPaymentTimeout<T>(
  promise: Promise<T>,
  stage: PaymentConfirmationTimeout["stage"],
  timeoutMs = PAYMENT_CONFIRM_TIMEOUT_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new PaymentConfirmationTimeout(stage)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Cached per publishable key so remounts don't re-download Stripe.js. */
const stripeCache = new Map<string, Promise<StripeJs | null>>();
function stripeFor(key: string) {
  if (!stripeCache.has(key)) stripeCache.set(key, loadStripe(key));
  return stripeCache.get(key) ?? null;
}

export function CardFeePayment(props: Props) {
  const [intent, setIntent] = useState<CardPaymentIntent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const { data, error } = await createCardPayment({
        programId: props.programId,
        attemptId: props.attemptId,
        intendedLoadAmountCents: props.intendedLoadAmountCents ?? 0,
        quantity: props.quantity ?? 1,
      });
      if (error || !data) {
        setError(error?.message ?? "Could not start payment");
        props.onPhaseChange?.("error");
        return;
      }
      setIntent(data);
      props.onPhaseChange?.(data.publishableKey ? "ready" : "error");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stripePromise = useMemo(
    () => (intent?.publishableKey ? stripeFor(intent.publishableKey) : null),
    [intent?.publishableKey],
  );

  if (error) {
    return (
      <p className="text-[14px] font-medium text-[#C41112]" data-testid="s7-payment-error">
        {error}
      </p>
    );
  }

  if (!intent) {
    return (
      <p className="text-[14px] text-foreground/60" data-testid="s7-payment-loading">
        Preparing secure payment…
      </p>
    );
  }

  if (!stripePromise) {
    return (
      <p className="text-[14px] font-medium text-[#C41112]" data-testid="s7-payment-error">
        Payment is not configured for this environment yet.
      </p>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret: intent.clientSecret, appearance: { theme: "flat" } }}
    >
      <PaymentInner {...props} intent={intent} />
    </Elements>
  );
}

function PaymentInner({
  intent,
  onAuthorised,
  onPhaseChange,
  submitting,
  buttonLabel,
  confirmingLabel,
  authorisedLabel,
  timeoutMessage,
  securityNotice,
}: Props & { intent: CardPaymentIntent }) {
  const stripe = useStripe();
  const elements = useElements();
  const [phase, setPhase] = useState<PaymentPhase>("ready");
  const [message, setMessage] = useState<string | null>(null);
  const activeConfirmation = useRef(false);

  function move(next: PaymentPhase) {
    setPhase(next);
    onPhaseChange?.(next);
  }

  async function authorise() {
    console.info("[payment-confirm] tap", { stripeReady: Boolean(stripe), elementsReady: Boolean(elements) });
    if (!stripe || !elements || activeConfirmation.current) return;
    activeConfirmation.current = true;
    move("confirming");
    setMessage(null);

    try {
      // The pinned control is rendered here, under this same Elements provider.
      // Flush and validate the mounted Payment Element before confirmation.
      console.info("[payment-confirm] elements_submit_called");
      const submitResult = await withPaymentTimeout(elements.submit(), "elements_submit");
      if (submitResult.error) {
        console.warn("[payment-confirm] elements_submit_rejected", { type: submitResult.error.type });
        setMessage(submitResult.error.message ?? "That payment could not be authorized.");
        move("error");
        return;
      }

      // 3DS challenges are handled in-place by Stripe.js; only redirect-only
      // methods would leave the page, and `if_required` keeps us in the funnel.
      console.info("[payment-confirm] confirm_payment_called");
      const { error, paymentIntent } = await withPaymentTimeout(
        stripe.confirmPayment({
          elements,
          redirect: "if_required",
          // The country field is intentionally hidden because this flow is US-only.
          // Stripe requires every hidden billing field to be supplied at confirm time.
          confirmParams: {
            payment_method_data: { billing_details: { address: { country: "US" } } },
          },
        }),
        "confirm_payment",
      );
      console.info("[payment-confirm] confirm_payment_returned", {
        status: paymentIntent?.status ?? null,
        errorType: error?.type ?? null,
      });

      if (error) {
        // Declined, insufficient funds, abandoned 3DS: the order is NOT created,
        // the intent stays reusable, and the customer can retry in place.
        // Recorded as a drop-off CAUSE, with the bank check kept apart from a
        // plain decline because the remedies are different.
        const abandonedCheck =
          error.code === "payment_intent_authentication_failure" ||
          error.decline_code === "authentication_required";
        recordSignal(abandonedCheck ? "three_ds_abandoned" : "payment_declined", {
          type: error.type ?? null,
          code: error.code ?? null,
          decline_code: error.decline_code ?? null,
        });
        setMessage(error.message ?? "That payment could not be authorized.");
        move("error");
        return;
      }

      if (paymentIntent?.status === "requires_capture") {
        move("authorised");
        await onAuthorised(paymentIntent.id);
        return;
      }

      const needsBankCheck = paymentIntent?.status === "requires_action";
      recordSignal(needsBankCheck ? "three_ds_abandoned" : "payment_declined", {
        status: paymentIntent?.status ?? null,
      });
      setMessage(
        needsBankCheck
          ? "Your bank needs to confirm this payment. Please complete the check and try again."
          : `Payment is ${paymentIntent?.status ?? "incomplete"} — nothing has been taken.`,
      );
      move("error");
    } catch (error) {
      const timedOut = error instanceof PaymentConfirmationTimeout;
      const stage = timedOut ? error.stage : "unexpected";
      console.error("[payment-confirm] failed", { stage, timedOut, errorName: error instanceof Error ? error.name : typeof error, errorMessage: error instanceof Error ? error.message : String(error) });
      if (timedOut) {
        Sentry.captureMessage("payment_confirmation_timeout", {
          level: "error",
          tags: { stage },
          extra: { paymentIntentId: intent.paymentIntentId },
        });
      } else {
        Sentry.captureException(error, { tags: { paymentStage: stage } });
      }
      setMessage(timedOut ? timeoutMessage : "That payment could not be authorized.");
      move("error");
    } finally {
      activeConfirmation.current = false;
    }
  }

  return (
    <div className="space-y-3">
      {/*
        N5/N6 · no "save my info for faster checkout" and no account creation —
        removed, not collapsed. Link enrolment is a Payment Element OPTION, not
        markup: `wallets.link: "never"` is the switch that keeps it away (hiding
        it in CSS is why it kept coming back). `terms.card: "never"` suppresses
        Stripe's saved-payment mandate text.

        Apple Pay / Google Pay are ON (`auto`). They ride on the existing
        card-only PaymentIntent — no server payment-method change — so the
        amount is the same server-resolved FEE ALONE on the wallet path as on
        the card path; this component cannot influence it either way.
        N7 · billing country defaults to the US.
      */}
      <PaymentElement
        options={{
          layout: "tabs",
          terms: { card: "never", applePay: "never", googlePay: "never" },
          wallets: { link: "never", applePay: "auto", googlePay: "auto" },
          fields: { billingDetails: { address: { country: "never" } } },
          defaultValues: { billingDetails: { address: { country: "US" } } },
        }}
      />


      {message && (
        <p className="text-[14px] font-medium text-[#C41112]" data-testid="s7-payment-message">
          {message}
        </p>
      )}

      <p
        className="text-[13px] leading-[1.5] text-foreground/60"
        data-testid="s7-security-notice"
      >
        {securityNotice}
      </p>

      <StickyBar>
        <PrimaryButton
          onClick={() => void authorise()}
          disabled={!stripe || !elements || phase === "confirming" || phase === "authorised" || submitting}
          className="basis-full"
          data-testid="s7-authorise"
        >
          {phase === "confirming"
            ? confirmingLabel
            : phase === "authorised"
              ? authorisedLabel
              : buttonLabel}
        </PrimaryButton>
      </StickyBar>
    </div>
  );
}
